import DateFormatter from "@/main/common/DateFormatter";
import {Card, CardContent, CardFooter} from "@/components/ui/card";
import {ArrowLeftRight, CalendarDays, MessageSquareQuote, MessageSquareText, UserPlus2, Users, UserX} from "lucide-react";
import React from "react";


export function isDisplayedAsGroupMember(u: UserProfileDTO | any): boolean {
    return (u)?.groupPublicId !== undefined
        || (u)?.isViewerBanned !== undefined
        || (u)?.requestPublicId !== undefined
        || (u)?.requestStatus !== undefined;
}

/* ----------------------- User Card ----------------------- */
import {
    GroupMemberDTO,
    UserProfileDTO,
    UserRequestingToJoinDTO
} from "@/generated-client";
import {Localizer} from "@/main/react/Types";
import { getSensitivePreferenceMode, resolveSensitiveMaskingConfig, type SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import {UserButtons} from "@/main/react/user/UserButtons";
import {type AnalyticsSurface} from "@/main/analytics/analyticsEvents";
import {CardMetadataBadges} from "@/main/react/card/CardMetadataBadges";
import {navigateToUrl} from "@/main/common/BrowserNavigation";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import { getDisplayUsername, getUserProfilePath, hasDisplayUsername, normalizeDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { getFriendlyCountBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import { MetaHandler } from "@/main/common/MetaHandler";
import { onProfileUpdated, onUserFollowAdded, onUserFollowRemoved } from "@/main/common/EventRegistry";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import { GroupLeadershipBadge, getLeadershipLabel, toGroupLeadershipLevel } from "@/main/react/group/GroupLeadershipBadge";
import { GroupMemberFlairChip } from "@/main/react/group/GroupMemberFlairChip";
import { DESTRUCTIVE_NOTICE_SURFACE_CLASS } from "@/main/react/component/destructiveNoticeSurface";

export function UserCardDisplayer(props: {
    user: UserProfileDTO;
    generalModelWrapper?: any;
    viewerIsLeaderOfGroup?: boolean;
    /**
     * The group this card is rendered inside, when the DTO cannot say so itself.
     *
     * <p>A `GroupMemberDTO` carries `groupPublicId`; a `UserRequestingToJoinDTO` does **not**, so on the
     * pending-requests list the group id resolved to `""` and every group-scoped control on the row was
     * inert. Needed by D1's ban affordance, which is offered on that row too. Falls back to the DTO's own
     * value, so member lists are unaffected whether or not they pass it.
     */
    groupPublicId?: string;
    sensitiveMasking?: SensitiveMaskingDirective;
    /** Where this card is rendered, forwarded to UserButtons for follow/unfollow attribution. */
    surface?: AnalyticsSurface;
    /**
     * Bulk-selection affordance (group members list, roadmap P2-C). Absent means the card is not
     * selectable, which is every surface except a leader in the members list's selection mode.
     */
    selection?: {
        selected: boolean;
        onToggle: () => void;
        disabled?: boolean;
        label: string;
        playwrightId?: string;
    };
    /**
     * The row's action is not the loudest thing this page offers (`GROUP-20`).
     *
     * <p>Passed straight through to `UserButtons`, which owns the variant and carries the full
     * reasoning. Set it where the PAGE has a primary action of its own that a column of identical
     * filled row buttons would out-shout — today that is the group Members section, whose own Join
     * sits in the rail. Every other call site (the profile follower and following lists, the
     * dashboard, the discussion page, the join-request queue) is unchanged by leaving it unset.
     */
    demotePrimaryAction?: boolean;
    /**
     * `GROUP-24`, operator decision **D22** — where this member's posts IN THIS GROUP live.
     *
     * <p>The roster prints two group-scoped facts per row ("8 posts", "0 thoughts") since `GROUP-9`
     * closed, and both are inert labels. The only link out of the row is the full-card overlay to that
     * person's SITE-WIDE profile, so the product knows exactly which posts in this group a member wrote,
     * prints the number, and then sends the reader out of the group to work the rest out by hand. D22's
     * answer is to make the count the beginning of a path.
     *
     * <p><b>Opt-in from the host, and it must stay that way.</b> This component also renders the profile
     * followers and following lists, the dashboard relationships lists, the group join-request queue,
     * the discussion participants roster and the component-gallery demo — seven render sites, and six of
     * them have no group to scope to. Supplying the address is the host's job for the same reason
     * `groupPublicId` is: only the surface knows. Absent, the badge is exactly the inert caption
     * `<span>` it is today, which is what `UserCardDisplayer.spec.tsx` asserts as an invariance rather
     * than as an assumption.
     *
     * <p>It is paired with a handler by the badge row, never supplied alone: `CardMetadataBadges`
     * upgrades a badge to an `<a>` only when it has BOTH, and that contract is load-bearing for
     * `EMPTY-STATES-85`, where withholding the handler is how a chip is deliberately made inert.
     *
     * <p>⚠️ **Nothing supplies this yet, deliberately.** The destination — the group Posts tab filtered
     * by author — does not exist: `AjaxPaginationController#discussionsInGroup` takes
     * `groupPublicId`, `cutoffAddedAt`, `orderingStyle` and `questionsOnly`, and the facet would be a
     * new predicate in `DiscussionRepositoryCustom.GroupDiscussionFeedQuery`'s native SQL. Linking
     * before that route exists is AGENTS.md rule 34's exact failure shape, so the seam ships and the
     * facet is parked with its cost written down on the register row.
     */
    groupScopedDiscussionsHref?: string;
    localizer: Localizer;
}) {
    const {user, viewerIsLeaderOfGroup = false, sensitiveMasking, surface = "page", selection, demotePrimaryAction = false, groupScopedDiscussionsHref, localizer} = props;
    const groupPublicIdFromProps = props.groupPublicId ?? "";
    const [displayedUser, setDisplayedUser] = React.useState(user);
    const userWithFlags = displayedUser as UserProfileDTO & { sensitive?: boolean | null };
    const df = React.useMemo(() => new DateFormatter(localizer), [localizer]);

    // detect group-specific fields and provide safe defaults
    const userIsDisplayedAsGroupMember = isDisplayedAsGroupMember(displayedUser);
    const groupPublicId = userIsDisplayedAsGroupMember
        ? ((displayedUser as GroupMemberDTO).groupPublicId ?? groupPublicIdFromProps)
        : "";
    const initiallyBanned = userIsDisplayedAsGroupMember ? Boolean((displayedUser as GroupMemberDTO).isBanned) : false;


    const requestPublicId = userIsDisplayedAsGroupMember && 'requestPublicId' in (displayedUser as any)
        ? ((displayedUser as UserRequestingToJoinDTO).requestPublicId ?? "")
        : "";
    const requestStatus = userIsDisplayedAsGroupMember && 'requestStatus' in (displayedUser as any)
        ? ((displayedUser as UserRequestingToJoinDTO).requestStatus ?? "")
        : "";
    // D3 (2026-08-14): this leader has blocked the requester, so `can_deliver_user_notification`
    // suppressed their join-request notification and the request has been sitting PENDING unseen. It is
    // deliberately NOT auto-rejected (D1 keeps block personal), so the awareness is restored here
    // instead. Narrowed structurally rather than off the generated client, which has not been
    // regenerated for this field yet — same precedent as `leadershipLevel` / `addedAt` below.
    const requestExtras = displayedUser as UserProfileDTO & { viewerHasBlockedRequester?: boolean | null };
    const viewerHasBlockedRequester = Boolean(requestPublicId) && Boolean(requestExtras.viewerHasBlockedRequester);
    // Group member cards carry the member's leadership tier + the date they joined *this group*
    // (group_leaders.leadership_level / group_members.added_at). These read-only fields ride on
    // the GroupMemberDTO projection; narrow structurally so this stays correct whether or not the
    // generated client has been regenerated yet.
    // The group-only extras, read off a cast for the same reason `leadershipLevel` and `addedAt` are:
    // they exist on `GroupMemberDTO` and nowhere else, and the generated client is regenerated on its
    // own schedule (it still carries a `banReason` the server stopped selecting in 2026-08).
    const memberExtras = displayedUser as UserProfileDTO & {
        leadershipLevel?: string | null;
        addedAt?: string | null;
        groupDiscussionsCount?: number | null;
        groupBoardMessagesCount?: number | null;
        // Round 7 slice 2b (D19-D21): posts + replies on this group's posts, and the leader-granted flair.
        groupContributionsCount?: number | null;
        flair?: string | null;
        // `DISCUSSION-37` (wave-14 Q23, operator decision D8). Rides on
        // `UserParticipatingInDiscussionDTO` and nowhere else, read off the same cast and for the same
        // reason as the two group counts above.
        discussionThoughtsCount?: number | null;
    };
    const memberLeadershipLevel = userIsDisplayedAsGroupMember ? toGroupLeadershipLevel(memberExtras.leadershipLevel) : null;
    // Round 7 slice 2b (D20). Local state because a leader's "Set flair" updates this row in place with the value
    // the server accepted; the list is not refetched. Seeded from the row, which is the only other copy.
    const [memberFlair, setMemberFlair] = React.useState<string | null>(
        userIsDisplayedAsGroupMember ? (memberExtras.flair ?? null) : null,
    );
    const groupJoinedAt = userIsDisplayedAsGroupMember ? memberExtras.addedAt : undefined;
    // `user.member-since` is "Joined {0}", and on a GROUP card that reads as "joined this group".
    // Falling back to the account creation date there published a SEAVIEN signup date as a
    // group-membership fact — most visibly on the pending join-request queue, where the one card whose
    // whole purpose is deciding whether this person may join announced "Joined 4 months ago" about
    // somebody who is not a member at all. Off a group surface the same key correctly means "joined
    // Seavien", so the fallback is kept there.
    //
    // Still open (needs a new key, so it carries a 24-locale bill): showing the REQUEST age from
    // `UserRequestingToJoinDTO.requestCreatedAt`, which the endpoint already returns and nothing
    // renders. Removing the false date is the half that costs no copy.
    const memberSinceSource = userIsDisplayedAsGroupMember ? groupJoinedAt : displayedUser.creationDate;
    const createdAtLabel = memberSinceSource ? df.timeSince(memberSinceSource) : "";
    // `GROUP-46` (C23, mega solo `mr2-group`): the "Still open" half above. A join-request card says how old
    // the REQUEST is, from the `requestCreatedAt` the endpoint already sends ("Asked to join 2 days ago").
    const requestCreatedAt = requestPublicId ? (displayedUser as UserRequestingToJoinDTO).requestCreatedAt : undefined;
    const requestAgeLabel = requestCreatedAt ? df.timeSince(requestCreatedAt) : "";
    const descriptionText = displayedUser.description?.trim() ?? "";
    const hasDescription = Boolean(descriptionText);
    const isSensitive = Boolean(userWithFlags.sensitive);
    const { sensitivePreference, hideSensitiveMedia } = resolveSensitiveMaskingConfig(getSensitivePreferenceMode(), sensitiveMasking);
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive }),
        [isSensitive, localizer],
    );
    const normalizedUsername = normalizeDisplayUsername(displayedUser.username);
    const displayUsername = getDisplayUsername(displayedUser.username, localizer);
    const primaryUsernameLabel = normalizedUsername ? `@${normalizedUsername}` : displayUsername;
    const profilePath = getUserProfilePath(displayedUser.username, "discussions");
    const loggedInUsername = MetaHandler.getLoggedInUsername();
    // A member owns their own profile card, so their own sensitive flag is never enforced against them.
    // Nothing had to be threaded from a page for this: the logged-in handle is already in the meta layer
    // and this component already reads it for the follow-count events below.
    const isViewerOwnProfile = Boolean(normalizedUsername) && normalizedUsername === loggedInUsername;

    React.useEffect(() => {
        setDisplayedUser(user);
    }, [user]);

    React.useEffect(() => {
        if (!normalizedUsername) {
            return;
        }

        const unsubscribeProfileUpdated = onProfileUpdated((payload) => {
            if (!payload || payload.username !== normalizedUsername) {
                return;
            }

            setDisplayedUser((previous) => ({
                ...previous,
                description: payload.profileDescription ?? undefined,
                profileImageUrl: payload.profileImageUrl ?? undefined,
                sensitive: payload.sensitive ?? (previous as UserProfileDTO & { sensitive?: boolean }).sensitive,
            } as UserProfileDTO));
        });

        const unsubscribeFollowAdded = onUserFollowAdded((payload) => {
            if (!payload) {
                return;
            }

            setDisplayedUser((previous) => {
                let nextUser = previous as UserProfileDTO;
                if (payload.targetUsername === normalizedUsername) {
                    nextUser = {
                        ...nextUser,
                        followersCount: (nextUser.followersCount ?? 0) + 1,
                    };
                    if (loggedInUsername && payload.actorUsername === loggedInUsername) {
                        nextUser = {
                            ...nextUser,
                            isViewerFollowing: true,
                        };
                    }
                }
                if (payload.actorUsername === normalizedUsername) {
                    nextUser = {
                        ...nextUser,
                        followingCount: (nextUser.followingCount ?? 0) + 1,
                    };
                }
                return nextUser;
            });
        });

        const unsubscribeFollowRemoved = onUserFollowRemoved((payload) => {
            if (!payload) {
                return;
            }

            setDisplayedUser((previous) => {
                let nextUser = previous as UserProfileDTO;
                if (payload.targetUsername === normalizedUsername) {
                    nextUser = {
                        ...nextUser,
                        followersCount: Math.max(0, (nextUser.followersCount ?? 0) - 1),
                    };
                    if (loggedInUsername && payload.actorUsername === loggedInUsername) {
                        nextUser = {
                            ...nextUser,
                            isViewerFollowing: false,
                        };
                    }
                }
                if (payload.actorUsername === normalizedUsername) {
                    nextUser = {
                        ...nextUser,
                        followingCount: Math.max(0, (nextUser.followingCount ?? 0) - 1),
                    };
                }
                return nextUser;
            });
        });

        return () => {
            unsubscribeProfileUpdated();
            unsubscribeFollowAdded();
            unsubscribeFollowRemoved();
        };
    }, [loggedInUsername, normalizedUsername]);
    // `GROUP-9` (operator decision D8, wave 8 2026-08-22). On a GROUP surface every badge is
    // group-scoped; everywhere else they stay site-wide.
    //
    // The row above these badges is already group-scoped on purpose: "Joined ... ago" reads
    // `GroupMemberDTO.addedAt`, and the comment on `memberSinceSource` records what happened the last
    // time this card mixed the two scopes. Nothing distinguished the badges from it, so on a members
    // page headed "20 posts" a member's post badge read as posts IN THIS GROUP - and the product was
    // one data shape away from member badges visibly summing above the group's own post count.
    //
    // D8 scopes the DATA rather than relabelling the badges: the server now returns
    // `groupDiscussionsCount` and `groupBoardMessagesCount` on the members projection. That is why
    // this costs no new keys - `badge.user.discussions` ("# posts") and `badge.user.thoughts`
    // ("# thoughts") are unqualified by design and are correct in both senses; the SURFACE is what
    // supplies the scope, which is exactly why leaving site-wide numbers under them was wrong.
    //
    // Followers/following are dropped here rather than scoped: a follow is not a group fact, there is
    // no group-scoped version of one to show, and they were the loudest half of the ambiguity.
    // Gated on the FIELD being present, not on `userIsDisplayedAsGroupMember`. That predicate is also
    // true of a pending join-request card, whose DTO is `UserRequestingToJoinDTO` and carries neither
    // count - so keying off it would leave the leader's request queue with an empty badge row and
    // strictly less to decide on than it has today. Rule 32's shape, one component down.
    const hasGroupScopedCounts = memberExtras.groupDiscussionsCount !== undefined
        || memberExtras.groupBoardMessagesCount !== undefined;
    /**
     * `DISCUSSION-37` (wave-14 **Q23**, operator decision **D8**): the discussion participants roster,
     * given the same treatment `GROUP-9` gave the group members list.
     *
     * <p>Every fact on a participant row was site-wide — joined date, posts authored anywhere,
     * followers, following, bio — so a tab named for the discussion answered a question about the
     * platform. Q23 resolves "which fact" to the shape `GROUP-9` chose: one scoped count, fetched
     * rather than relabelled (`UserParticipatingInDiscussionDTO.discussionThoughtsCount`).
     *
     * <p>Both of `GROUP-9`'s recorded traps are reused deliberately:
     * <ul>
     *   <li>gated on the FIELD being present, never on a card-context predicate — the participants
     *       card is the same component the followers and search lists render, and keying off the
     *       surface would put an empty badge row on whichever list the server has not shipped the
     *       field to;</li>
     *   <li>`showZero`, because "0 thoughts here" is the answer somebody came for and must not look
     *       like a count that failed to load. A MISSING count still drops the badge.</li>
     * </ul>
     *
     * <p>Followers/following go for `GROUP-9`'s reason: a follow is not a fact about this
     * conversation, and they were the loudest half of the ambiguity. Zero new keys —
     * `badge.user.thoughts` is unqualified by design and the surface supplies the scope.
     */
    const hasDiscussionScopedCount = memberExtras.discussionThoughtsCount !== undefined;
    // `GROUP-24` / D22 — the group-scoped POSTS count is the one badge on this card that can become a
    // path, and only when the host has told us where that path goes. Written as a variable rather than
    // inline so the two shapes it can take are visible in one place: `undefined` (every other surface,
    // and the group roster until the author facet exists) leaves the badge a caption, and an address
    // turns it into the anchor `CardMetadataBadges` already knows how to render.
    const groupDiscussionsRoute = groupScopedDiscussionsHref?.trim() || undefined;
    // `DASHBOARD-39` (operator decisions D38 + D1, slice `2a`): the "Mutual" chip. It states the one
    // relationship fact the follow button beneath it cannot: that this person follows the viewer back.
    //
    // Both directions ride on every row of BOTH relationship queries (`findUsersFollowedBy` and
    // `findFollowersOfUser`, slice `1c`), so the card never needs to know which tab or which surface it
    // is on. That is why the dashboard's single `RelationshipsList` call site and the profile's two
    // lists pass nothing new. Every other query mapping `UserProfileDTO` leaves the reciprocal null
    // ("not asked for"), and null is no chip, so the group roster, the join queue, the participants
    // roster and search are unchanged by construction.
    //
    // `isViewerFollowing` is the half the follow events above keep live, so "Follow back" on a
    // followers row makes the chip appear and an unfollow removes it, without a refetch.
    //
    // Read off a cast for the same reason as `viewerHasBlockedRequester`: the generated client has not
    // been regenerated since the field landed server-side.
    const relationshipFlags = displayedUser as UserProfileDTO & { isFollowingViewer?: boolean | null };
    const isMutualFollow = displayedUser.isViewerFollowing === true && relationshipFlags.isFollowingViewer === true;
    const mutualItems = isMutualFollow ? [
        {
            key: "mutual",
            label: localizer("badge.user.mutual"),
            icon: ArrowLeftRight,
            dataPlaywrightId: "user-card-mutual",
        },
    ] : [];
    const statItems = [...(hasDiscussionScopedCount ? [
        {
            key: "discussion-thoughts",
            label: getFriendlyCountBadgeLabel(localizer, "badge.user.thoughts", memberExtras.discussionThoughtsCount, { showZero: true }),
            icon: MessageSquareQuote,
        },
    ] : hasGroupScopedCounts ? [
        {
            key: "group-discussions",
            // `showZero`, unlike every site-wide badge: on a members list "0 posts here" is the answer
            // a visitor came for, and it is the number the page's own header can be checked against.
            // A MISSING count still drops the badge, so a server that has not shipped the field yet
            // renders no badge rather than a false zero.
            label: getFriendlyCountBadgeLabel(localizer, "badge.user.discussions", memberExtras.groupDiscussionsCount, { showZero: true }),
            icon: MessageSquareText,
            href: groupDiscussionsRoute,
        },
        // `GROUP-105` / `GROUP-116` (U25, mega solo `mr2-group`): the group member card keeps ONE count, the
        // posts line. The board-message chip ("replies") went: it counted board messages under a site-wide
        // label and never added up with "contributions". Contributions moved beside the flair in the name row
        // (below), because it is the member's standing, not a third stat chip.
    ] : [
        {
            key: "discussions",
            label: getFriendlyCountBadgeLabel(localizer, "badge.user.discussions", displayedUser.discussionsAuthoredCount),
            icon: MessageSquareText,
        },
        {
            key: "followers",
            label: getFriendlyCountBadgeLabel(localizer, "badge.user.followers", displayedUser.followersCount),
            icon: Users,
        },
        {
            key: "following",
            label: getFriendlyCountBadgeLabel(localizer, "badge.user.following", displayedUser.followingCount),
            icon: UserPlus2,
        },
    ]), ...mutualItems].filter((item) => Boolean(item.label));


    return (
        <Card
            // A stable per-card handle so a browser spec can act on ONE person's row — the member whose
            // menu it opens, the request it flags — instead of reaching for the card through its class
            // string or an ancestor xpath off the username text. Both of those break on any layout edit,
            // and a locator that silently starts matching the wrong card fails as a confusing timeout
            // rather than as "wrong row".
            data-playwright-id={normalizedUsername ? `user-card-${normalizedUsername}` : undefined}
            /* The card is a COLUMN at every width: identity and bio take the full card, the action
               cluster takes its own row underneath. That is the shape of every other card family here
               (`GroupCardDisplayer`, `SeriesCardDisplayer`, `ThoughtCardDisplayer`,
               `DiscussionCardDisplayer` all pair a full-width `CardContent` with a `pt-0 justify-end`
               `CardFooter`), and this card is the only one that ever diverged.

               It diverged between 2026-08-17 and 2026-08-27 under `DISCUSSION-3`, which added
               `sm:flex-row sm:items-center` so the actions would share the identity line on desktop.
               **Reverted by operator decision.** `DISCUSSION-3` was filed and closed against the
               PARTICIPANTS list, where a member has no bio and the row genuinely was mostly empty. This
               component also backs the group members list, where every row HAS a bio — and there the
               inline cluster reserved about a quarter of the card width for itself, so the bio wrapped
               four lines early against a column of buttons sitting in vertical dead space.

               The reusable lesson, and the reason this comment is long: **a card whose emptiness is the
               complaint has to be judged on its FULLEST state, not its emptiest.** A breakpoint cannot
               see content, so it cannot make that distinction; if the sparse row is taken up again, the
               shape is a variant prop keyed on whether the card actually has a bio and stat chips. Do
               not re-add `sm:flex-row` here — `UserCardDisplayer.spec.tsx` pins its absence. */
            className="relative flex w-full max-w-full flex-col overflow-hidden rounded-xl border border-border py-0 card-hover gap-0"
        >
            {/* The bulk-selection checkbox is a leader's management control, not content, so it lives
                OUTSIDE the sensitive guard — exactly like the CardFooter actions below. Inside the
                guard a masked card would make it both inert (`pointer-events-none` on the guarded
                content) and covered (the overlay spans `inset-0`), leaving a leader unable to select a
                sensitive-flagged member for bulk removal. Its padding mirrors the CardContent inset so
                the row lines up as if it were still the leading flex item. */}
            <div className="flex w-full min-w-0 items-start">
                {selection ? (
                    <div className="relative z-layer-sticky shrink-0 pl-3 pt-4 sm:pl-4 sm:pt-5">
                        <input
                            type="checkbox"
                            checked={selection.selected}
                            disabled={selection.disabled}
                            onChange={selection.onToggle}
                            aria-label={selection.label}
                            data-playwright-id={selection.playwrightId}
                            /* 17.5 CSS px painted — the smallest control on the members screen and the
                             * only way to select a member for bulk removal or ban, while every other
                             * control on the same screen already measures 44. The hit expander is the
                             * sanctioned path here because the painted box must NOT grow: an 18px
                             * checkbox rendered at 44 would push the whole content column right.
                             * The `relative` is load-bearing — the utility deliberately sets no
                             * `position`, so without it the expander would anchor to the padded
                             * wrapper instead of the checkbox and land off-centre
                             * (`tapTargets.contract.test.tsx` fails if it is dropped).
                             * Ring moved to the one sanctioned spec (rule 4). */
                            className="relative touch-hit-44 size-4.5 shrink-0 cursor-pointer rounded border-border accent-primary focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                    </div>
                ) : null}
                <CardContent className="min-w-0 flex-1 px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex min-w-0 flex-col gap-3">
                        <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                                {/* The guard covers the AVATAR and nothing else. It used to wrap the
                                    whole CardContent, so a sensitive-flagged member lost their handle,
                                    their leadership badge, their joined date, their bio and their stats
                                    — their identity, not their expressive media — while the footer
                                    outside it kept Follow and Direct message fully live. The Members tab
                                    exists to answer "who is in this group", and for those rows it
                                    answered nothing while still offering three ways to act on the person
                                    it would not name (`GROUP-3`, `DISCUSSION-19`).

                                    "The avatar" is not a judgement call either: it is what
                                    `settings.update-profile-settings.sensitive.help` promises the member
                                    when they set the flag — their avatar masked and their profile page
                                    gated. The page gate is what withholds the person's content; a card
                                    withholds their picture. */}
                                <SensitiveMediaGuard
                                    className="shrink-0 rounded-full"
                                    isSensitive={isSensitive}
                                    hideSensitiveMedia={hideSensitiveMedia}
                                    sensitivePreference={sensitivePreference}
                                    isViewerOwner={isViewerOwnProfile}
                                    variant="avatar"
                                    // The full-card link below is a sibling of this guard, so without this the masked scrim
                                    // covers it and a blurred card cannot be opened without revealing first. Design §8.6.
                                    overlayStacking="below-link-overlay"
                                    badgeLabel={warningCopy.badgeLabel}
                                    label={warningCopy.label}
                                    helperText={warningCopy.helperText}
                                    revealText={warningCopy.revealText}
                                    preferencesRouteText={warningCopy.preferencesRouteText}
                                >
                                    <ProfileImage localizer={localizer}
                                        imageUrl={displayedUser.profileImageUrl}
                                        username={normalizedUsername ?? undefined}
                                        hoverable={true}
                                        interactive={true}
                                        className={"size-15 sm:size-16"}
                                        mode={"user"}
                                    />
                                </SensitiveMediaGuard>

                                <div className="user-overview flex min-w-0 flex-1 flex-col gap-2">
                                    <div className="inline-flex max-w-full flex-wrap items-center gap-2 text-base font-semibold leading-tight tracking-tight text-text-main sm:text-lg">
                                        <span className="truncate" translate={hasDisplayUsername(displayedUser.username) ? "no" : undefined}>{primaryUsernameLabel}</span>
                                        {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                                        {memberLeadershipLevel ? <GroupLeadershipBadge label={getLeadershipLabel(localizer, memberLeadershipLevel)} /> : null}
                                        <GroupMemberFlairChip flair={memberFlair} />
                                        {/* Round 7 slice 2b (D19/D21) count, moved here by U25: posts + replies whose post
                                            is in this group. Gated on the field, so the join-request queue renders none. */}
                                        {typeof memberExtras.groupContributionsCount === "number" ? (
                                            <span className="text-xs font-normal text-muted-foreground" data-playwright-id="user-card-group-contributions">
                                                {localizer("group.member.contributions", memberExtras.groupContributionsCount)}
                                            </span>
                                        ) : null}
                                    </div>
                                    {createdAtLabel || requestAgeLabel ? (
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground sm:text-sm">
                                            <div className="inline-flex items-center gap-1.5">
                                                <CalendarDays className="size-3.5" aria-hidden={true}/>
                                                {requestAgeLabel ? (
                                                    <span data-playwright-id="user-card-request-age">{localizer("groups.request.asked_at", requestAgeLabel)}</span>
                                                ) : (
                                                    <span>{localizer("user.member-since", createdAtLabel)}</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                        </div>

                        {viewerHasBlockedRequester ? (
                            <div
                                data-playwright-id="join-request-blocked-requester-notice"
                                className={`flex items-start gap-2 rounded-xl px-3 py-2 text-sm text-text-main ${DESTRUCTIVE_NOTICE_SURFACE_CLASS}`}
                            >
                                <UserX className="mt-0.5 size-4 shrink-0 text-destructive-text" aria-hidden={true}/>
                                <span>{localizer("groups.request.blocked_requester.notice")}</span>
                            </div>
                        ) : null}

                        {hasDescription ? (
                            <p className="text-sm leading-5 text-muted-foreground sm:text-[15px]">
                                {descriptionText}
                            </p>
                        ) : null}

                        <CardMetadataBadges
                            className="gap-1.5"
                            // U25: on a group member card the one remaining count is a muted text line, not
                            // a bordered chip, so every card has one height whatever the digits.
                            badgeClassName={hasGroupScopedCounts && !hasDiscussionScopedCount
                                ? "border-transparent bg-transparent px-0 text-muted-foreground"
                                : "border-border/60 bg-background2/50"}
                            items={statItems.map((item) => {
                                const Icon = item.icon;
                                // `GROUP-24` / D22. `CardMetadataBadges` forks on the HANDLER, and
                                // upgrades to an `<a href>` only when it also has an address — so a
                                // badge with neither stays the inert caption `<span>` every other
                                // surface renders today, and this mapper cannot accidentally promote
                                // one. The handler is what makes an ordinary primary click a soft
                                // navigation; every other click (middle, modified, keyboard "copy link
                                // address") falls through to the real `href` on the element.
                                const href = "href" in item ? item.href : undefined;

                                return {
                                    key: item.key,
                                    icon: <Icon className="size-3.5" aria-hidden={true}/>,
                                    label: item.label,
                                    href,
                                    onClick: href
                                        ? () => navigateToUrl(href, {source: "user-card-group-posts-badge"})
                                        : undefined,
                                    dataPlaywrightId: "dataPlaywrightId" in item
                                        ? item.dataPlaywrightId
                                        : href ? `user-card-stat-${String(item.key)}` : undefined,
                                };
                            })}
                        />
                    </div>
                </CardContent>
            </div>

            {/* `focus-visible` treatment with a NEGATIVE outline offset (`EMPTY-STATES-41`). This anchor
                is the only way to open the row with a keyboard and carried no focus treatment at all.
                The negative offset is the load-bearing half: the card root is `overflow-hidden` and an
                outline at offset 0 paints outside the border box, where it can be clipped away
                entirely. One string across all five card families. */}
            {profilePath ? (
                <a
                    href={profilePath}
                    className="absolute inset-0 z-layer-base-front rounded-xl focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                    aria-label={displayUsername}
                ></a>
            ) : null}

            {/* The action cluster's own row, at the bottom of the card, at every width. The class string
                is deliberately the one `GroupCardDisplayer` uses, so the two footers align across a mixed
                grid row; `mt-auto` is the only difference, and only because that card sits in a
                fixed-height grid cell and this one does not. See the root's note before changing it. */}
            <CardFooter className="relative z-layer-raised-front flex flex-row flex-wrap items-center justify-end gap-2 px-3 pb-3 pt-0 sm:px-4 sm:pb-4">
                {hasDisplayUsername(displayedUser.username) ? (
                    <UserButtons
                        username={normalizedUsername ?? ""}
                        initiallyFollowing={Boolean(displayedUser.isViewerFollowing)}
                        groupPublicId={groupPublicId}
                        initiallyBanned={initiallyBanned}
                        initiallyViewerIsLeader={viewerIsLeaderOfGroup}
                        requestPublicId={requestPublicId}
                        requestStatus={requestStatus}
                        demotePrimaryAction={demotePrimaryAction}
                        surface={surface}
                        flair={memberFlair}
                        // Only a member row can carry a flair; a join-request row (`requestPublicId`) cannot.
                        onFlairChange={userIsDisplayedAsGroupMember && groupPublicId && !requestPublicId ? setMemberFlair : undefined}
                        localizer={localizer}
                    />
                ) : null}
            </CardFooter>

        </Card>
    );
}

